import { useQuery } from '@tanstack/react-query'
import { History, ArrowRight } from 'lucide-react'
import type { UserListItem, RoleHistoryResponse } from '@common/types'
import { fetchRoleHistory } from '@frontend/lib/api'
import { RoleBadge } from '@frontend/components/ui/role-badge'
import { Spinner } from '@frontend/components/ui/spinner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'

interface RoleHistoryModalProps {
  user: UserListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function RoleHistoryModal({ user, open, onOpenChange }: RoleHistoryModalProps) {
  const { data, isLoading, error } = useQuery<RoleHistoryResponse>({
    queryKey: ['user-history', user?.userId],
    queryFn: () => fetchRoleHistory(user!.userId),
    enabled: open && !!user,
  })

  const history = data?.history ?? []

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Role History
          </DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">Failed to load history.</p>
              <p className="mt-1 text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : history.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">No role changes recorded yet.</div>
          ) : (
            <div className="space-y-4">
              {history.map((item) => (
                <div key={item.id} className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center gap-2 mb-2">
                    {item.previousRole ? (
                      <>
                        <RoleBadge role={item.previousRole} />
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <RoleBadge role={item.newRole} />
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-muted-foreground">(created)</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <RoleBadge role={item.newRole} />
                      </>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>
                      <span className="font-medium">Changed by:</span> {item.changedBy.email}
                    </div>
                    <div>
                      <span className="font-medium">Date:</span> {formatDateTime(item.changedAt)}
                    </div>
                    {item.reason && (
                      <div>
                        <span className="font-medium">Reason:</span> {item.reason}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
